/**
 * T17300 · SSR / pure-Node guard for `applySdkDefaults`.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * — `applySdkDefaults()` MUST throw if invoked in a pure-Node SSR
 * context (no `document`, no `window`). Replaces the silent no-op with an
 * explicit `SdkError` so a misuse is caught at bootstrap, not silently at
 * first render.
 *
 * The SSR-correct path uses `ssrPrefetch.*` + `<SsrHydrate>` from
 * `@morfi/backend-sdk-v2/server`. `applySdkDefaults` is `'use client'`-
 * only.
 */

import { SdkError } from '../../errors/SdkError.js';

export function isPureNodeSSR(): boolean {
  return typeof window === 'undefined' && typeof document === 'undefined';
}

/**
 * Throws the canonical `SDK_SSR_INIT` SdkError when called in pure-Node
 * SSR context. Caller invokes this as the first line of `applySdkDefaults`.
 */
export function assertNotServer(): void {
  if (isPureNodeSSR()) {
    throw new SdkError({
      category: 'precondition_failed',
      code: 'SDK_SSR_INIT',
      userMessage: 'sdk.bootstrap.ssr_not_supported',
      devMessage:
        'applySdkDefaults() called in pure-Node SSR context. Use ssrPrefetch.* + ' +
        "<SsrHydrate> from '@morfi/backend-sdk-v2/server' for server rendering. This " +
        'entry point is client-only.',
      correlationId: '',
    });
  }
}
